(ns practitest.frontend.components2.dashboard.charts.render-helpers
  (:require [clojure.string :as str]))

(defn update-svg-scale!
  "Set --svg-scale CSS custom property: screen pixels per viewBox unit.

   Problem: pie labels live inside <foreignObject>. The browser renders
   foreignObject content in viewBox coordinates, then scales the whole
   SVG to fit its container. So `font-size: 12px` does not mean 12 screen
   pixels — it means 12 viewBox units, which grow or shrink with the SVG.

   Solution: --svg-scale = clientWidth (actual pixels on screen)
                         / viewBoxWidth (internal SVG coordinate space).
   This is how many screen pixels one viewBox unit maps to. The CSS uses
   font-size: calc(12px / var(--svg-scale, 1)) to divide out the scaling,
   keeping labels at a constant 12px on screen regardless of SVG size.

   Example: viewBox width=400, rendered at 800px → --svg-scale=2
   → CSS sets font-size=6px → browser scales 6*2 = 12px on screen."
  [svg-element]
  (let [vb-w (some-> svg-element .-viewBox .-baseVal .-width)]
    (when (and vb-w (pos? vb-w))
      (.setProperty (.-style svg-element) "--svg-scale"
                    (str (/ (.-clientWidth svg-element) vb-w))))))

(def ^:private instance-resolved-props
  "Computed properties whose value is a per-instance resolved size/position/color"
  #{"width" "height" "fill" "transform" "transform-origin" "x" "y" "cx" "cy" "r"})

(defn- computed-css-text
  "Build a CSS declaration string from an element's computed style, leaving out skip-props."
  [el skip-props]
  (let [computed (js/getComputedStyle el)
        len      (.-length computed)]
    (loop [i 0 acc ""]
      (if (>= i len)
        acc
        (let [prop (.item computed i)]
          (recur (inc i)
                 (if (contains? skip-props prop)
                   acc
                   (str acc prop ": " (.getPropertyValue computed prop) "; "))))))))

(defn embed-styles!
  "Read computed CSS for selectors from an in-DOM SVG and embed them as a
   <style> element in a target SVG (typically a detached clone).
   Options:
     :extra-css  — CSS appended after the embedded rules
     :keep-props — instance-resolved props (e.g. \"fill\") to embed anyway"
  ([source-svg target-svg selectors]
   (embed-styles! source-svg target-svg selectors nil))
  ([source-svg target-svg selectors {:keys [extra-css keep-props]}]
   (let [skip-props (reduce disj instance-resolved-props keep-props)
         css        (reduce
                     (fn [acc selector]
                       (if-let [el (.querySelector source-svg selector)]
                         (str acc selector " { " (computed-css-text el skip-props) " }\n")
                         acc))
                     "" selectors)
         style-el   (.createElementNS js/document "http://www.w3.org/2000/svg" "style")]
     (set! (.-textContent style-el) (str css extra-css))
     (.prepend target-svg style-el))))

(defn pad-viewbox!
  "Expand an SVG's viewBox"
  [svg-element pad]
  (when-let [view-box (.getAttribute svg-element "viewBox")]
    (let [[x y w h] (map js/parseFloat (.split view-box " "))]
      (.setAttribute svg-element "viewBox"
                     (str/join " " [(- x pad) y (+ w (* 2 pad)) h])))))

(defn ensure-group!
  "Reuse existing <g> by class name, or append a new one."
  ([parent class-name]
   (let [existing (.select parent (str "g." class-name))]
     (if (.empty existing)
       (-> parent (.append "g") (.attr "class" class-name))
       existing)))
  ([parent class-name transform]
   (let [existing (.select parent (str "g." class-name))]
     (if (.empty existing)
       (-> parent (.append "g") (.attr "class" class-name) (.attr "transform" transform))
       existing))))

(defn- resolve-css-vars-in-svg!
  [clone source-svg]
  (let [computed (js/getComputedStyle source-svg)]
    (doseq [el (array-seq (.querySelectorAll clone "*"))
            attr ["fill" "stroke" "color"]]
      (when-let [[_ var-name]
                 (some-> (.getAttribute el attr)
                         (#(re-matches #"var\((--[^)]+)\)" %)))]
        (when-let [resolved (not-empty (str/trim (.getPropertyValue computed var-name)))]
          (.setAttribute el attr resolved))))))

(defn clone-svg
  "Clone an in-DOM SVG element with explicit width/height attributes
   set from its bounding rect. Returns a detached clone ready for
   serialization."
  [svg-element]
  (let [clone    (.cloneNode svg-element true)
        svg-rect (.getBoundingClientRect svg-element)]
    (.setAttribute clone "width" (.-width svg-rect))
    (.setAttribute clone "height" (.-height svg-rect))
    (resolve-css-vars-in-svg! clone svg-element)
    clone))

(defn- view-box [svg-element]
  (let [vb (some-> svg-element .-viewBox .-baseVal)]
    {:x (or (some-> vb .-x) 0)
     :y (or (some-> vb .-y) 0)
     :w (or (some-> vb .-width) 0)
     :h (or (some-> vb .-height) 0)}))

(defn- append-children-clone!
  "Append deep clones of source's children to target, wrapped in a <g>
   that puts the source's viewBox origin at (x, y)."
  [target source x y]
  (let [{vb-x :x vb-y :y} (view-box source)
        g                 (.createElementNS js/document "http://www.w3.org/2000/svg" "g")]
    (.setAttribute g "transform" (str "translate(" (- x vb-x) "," (- y vb-y) ")"))
    (doseq [child (array-seq (.-childNodes source))]
      (.appendChild g (.cloneNode child true)))
    (.appendChild target g)))

(defn clone-split-svg
  "Merge the split layout's svgs (fixed y-axis, scrollable plot, legend) in
   frame into one detached svg, so it holds the whole chart rather than only
   the part visible in the scroll container. Each part is drawn at its viewBox size.
   Ready for serialization, like clone-svg."
  [frame]
  (let [[y-axis-svg plot-svg legend-svg] (mapv #(.querySelector frame %)
                                               [".chart-y-axis" ".chart-scroll > svg.chart" ".chart-legend"])
        {y-axis-w :w}             (view-box y-axis-svg)
        {plot-w :w plot-h :h}     (view-box plot-svg)
        {legend-w :w legend-h :h} (view-box legend-svg)
        width                     (+ y-axis-w (max plot-w legend-w))
        height                    (+ plot-h legend-h)
        clone                     (.cloneNode plot-svg false)]
    (append-children-clone! clone y-axis-svg 0 0)
    (append-children-clone! clone plot-svg y-axis-w 0)
    (append-children-clone! clone legend-svg y-axis-w plot-h)
    (doto clone
      (.setAttribute "viewBox" (str "0 0 " width " " height))
      (.setAttribute "width" width)
      (.setAttribute "height" height))
    (resolve-css-vars-in-svg! clone frame)
    clone))

(defn- text-length!
  [^js text-el s]
  (set! (.-textContent text-el) s)
  (.getComputedTextLength text-el))

(defn ellipsize-label!
  [^js text-el full max-w]
  (let [overflow? (> (text-length! text-el full) max-w)]
    (when overflow?
      (let [n (loop [lo 0 hi (dec (count full))]
                (if (< lo hi)
                  (let [mid (quot (+ lo hi 1) 2)]
                    (if (<= (text-length! text-el (str (subs full 0 mid) "…")) max-w)
                      (recur mid hi)
                      (recur lo (dec mid))))
                  lo))]
        (text-length! text-el (str (subs full 0 n) "…"))))
    overflow?))
